// ─────────────────────────────────────────────────────────────────────────────
// ImagingTest.tsx
//
// Top-level demo page: a 3-step portal-style flow.
//
//   PRE  → pick reader/camera options, wait for SDK scanner readiness
//   SCAN → SDK-owned camera + capture + overlay + scan loop
//   POST → demo-owned results table + profile chart + PMF story panel
//
// The SDK owns scanner mechanics. The demo owns workflow, cassette selection,
// and result display.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from 'react';

import { useNDXReader } from '@novarumdx/ndx-imaging-web/react';
import type { PmfStoryDiagnostics, EngineConfig, TestConfig } from '@novarumdx/ndx-imaging-web';
import {
  resolveStoryImages,
  clearAllPmfStoryImages,
  generateSessionId,
} from '@novarumdx/ndx-imaging-web';

import PreScanPage from './PreScanPage';
import ScanPage from './ScanPage';
import type { ReaderAspect } from './ScanPage';
import PostScanPage from './PostScanPage';
import { DEFAULT_HOUSINGS } from './readers';
import type { HousingConfig } from './readers';
import type { CompletedScan } from './exportResult';
import { deleteScan } from './exportResult';


type Page = 'pre' | 'scan' | 'post';
type PreviewMode = 'fit' | 'fill';

const PREVIEW_MODE_STORAGE_KEY = 'ndx-imaging-web-demo.previewMode';

function readPreviewMode(): PreviewMode {
  try {
    return window.sessionStorage.getItem(PREVIEW_MODE_STORAGE_KEY) === 'fit' ? 'fit' : 'fill';
  } catch {
    return 'fill';
  }
}


const ImagingTest: React.FC = () => {
  const [page, setPage] = useState<Page>('pre');
  const [housing, setHousing] = useState<HousingConfig>(DEFAULT_HOUSINGS[0]);
  const [cameraDeviceId, setCameraDeviceId] = useState('');
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [hScale, setHScale] = useState<number | undefined>(undefined);
  const [framesToCapture, setFramesToCapture] = useState<number>(10);
  const [collectionMode, setCollectionMode] = useState<boolean>(false);
  const [resolvedCassetteConfigUrl, setResolvedCassetteConfigUrl] = useState<string | undefined>(undefined);
  const [cassetteConfigError, setCassetteConfigError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>(readPreviewMode);
  const [readerAspect, setReaderAspect] = useState<ReaderAspect>('4:3');
  const [completedScans, setCompletedScans] = useState<CompletedScan[]>([]);
  const [pmfDiagnostics, setPmfDiagnostics] = useState<PmfStoryDiagnostics | null>(null);

  // On mount: clear any orphaned IndexedDB images from previous sessions
  // that were never cleaned up (e.g. browser was closed mid-scan).
  useEffect(() => {
    clearAllPmfStoryImages()?.catch(() => {
      /* best effort */
    });
  }, []);

  // Keep refs to the latest values so onSuccess closures always read
  // values that were active when that scan ran (avoids stale closure).
  const hScaleRef = useRef(hScale);
  useEffect(() => { hScaleRef.current = hScale; }, [hScale]);

  const framesToCaptureRef = useRef(framesToCapture);
  useEffect(() => { framesToCaptureRef.current = framesToCapture; }, [framesToCapture]);

  // Full testConfig object — updated when housing changes.
  const testConfigRef = useRef<TestConfig | undefined>(undefined);
  const cassetteConfigReady = Boolean(housing.config && resolvedCassetteConfigUrl === housing.config);

  // Resolve the cassette settings before initialising the reader. nFrames and
  // collectionMode are explicit SDK overrides, so constructing the reader with
  // temporary UI defaults would override the cassette until this fetch completes.
  useEffect(() => {
    const controller = new AbortController();
    setResolvedCassetteConfigUrl(undefined);
    setCassetteConfigError(null);
    testConfigRef.current = undefined;

    if (!housing.config) return () => controller.abort();

    fetch(housing.config, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`Cassette configuration was not found (HTTP ${r.status}).`);
        }
        return r.json();
      })
      .then((d: { testConfig?: TestConfig }) => {
        if (controller.signal.aborted) return;
        const tc = d.testConfig ?? {};
        testConfigRef.current = tc;
        setFramesToCapture(tc.framesToCapture ?? 10);
        setHScale(tc.pmfInitialHScaleFactor ?? undefined);
        setCollectionMode(tc.collectionMode ?? false);
        setResolvedCassetteConfigUrl(housing.config);
        // visualizationMode is read by NDXReaderView from scanner.visualizationMode
        // (set by the worker on init), so no local state override is needed.
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          console.warn('[ImagingTest] failed to load testConfig:', e);
          const reason = e instanceof Error ? e.message : 'Unknown error.';
          setCassetteConfigError(`Could not load ${housing.label}. ${reason}`);
        }
      });

    return () => controller.abort();
  }, [housing.config]);

  const scanner = useNDXReader({
    // Supplying no config prevents the hook from loading the engine. Once the
    // cassette settings are resolved below, this render supplies one coherent
    // set of explicit overrides for the initial reader construction.
    config: cassetteConfigReady ? housing.config : undefined,
    hScale,
    nFrames: framesToCapture,
    collectionMode,
    previewMode,
    // '' (no specific camera selected) means "use the default camera" — pass
    // undefined so the SDK config validator doesn't reject an empty deviceId
    // (an empty string triggers config-error, so init is never posted and the
    // pre-scan/Begin Scan spinner hangs).
    cameraDeviceId: cameraDeviceId || undefined,
    torchEnabled,
    testName: housing.label ?? 'demo',
    pmfStoryConfig: {
      enabled: true,
      captureImages: true,
      captureErrorFrameImages: true,
    },
    onSuccess: async (strips, pmfStory, diagnostics, analysisMs) => {
      const resolved = await resolveStoryImages(pmfStory);
      setPmfDiagnostics(diagnostics);

      // Derive sessionId from the first image key.
      const firstImageUrl = pmfStory.find((f) => f.imageUrl)?.imageUrl;
      const sessionId = firstImageUrl
        ? firstImageUrl.split('/')[0]
        : generateSessionId(housing.label ?? 'demo');

      const tc = testConfigRef.current
        ? { ...testConfigRef.current, collectionMode }
        : undefined;

      // Snapshot the engine config at the time this scan completed.
      const engineConfig: EngineConfig = {
        width: 640,
        height: 480,
        pmf_model: '', // not available in unified path — unused by serialization
        pmf_clf: '',   // not available in unified path — unused by serialization
        n_frames: framesToCaptureRef.current,
        ctrl_corr: tc?.controlLineCorrection ?? true,
        prof_align: tc?.profileAlignment ?? true,
        tracker_enabled: true,
        baseline_mse_t: tc?.controlBaselineThreshold ?? 2.5,
        exp_min: 45,
        exp_max: 250,
        baseline_order: new Uint8Array([tc?.polynomialBaselineOrder ?? 5]),
        prof_mode: 0,
        ...(hScaleRef.current !== undefined && { init_h_scale: hScaleRef.current }),
      };

      const newScan: CompletedScan = {
        sessionId,
        strips,
        engineConfig,
        testConfig: tc,
        pmfStory: resolved,
        analysisMs,
        completedAt: Date.now(),
      };

      setCompletedScans((prev) => [...prev, newScan]);
      setPage('post');
    },
  });

  const handlePreviewModeChange = (mode: PreviewMode) => {
    setPreviewMode(mode);
    try {
      window.sessionStorage.setItem(PREVIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  };

  const handleReaderAspectChange = (aspect: ReaderAspect) => {
    setReaderAspect(aspect);
  };

  const startScan = () => {
    if (!cassetteConfigReady || !scanner.engineReady) return;
    scanner.reset();
    setPage('scan');
  };

  const handleDeleteScan = async (sessionId: string) => {
    const scan = completedScans.find((s) => s.sessionId === sessionId);
    if (scan) {
      await deleteScan(scan);
      setCompletedScans((prev) => prev.filter((s) => s.sessionId !== sessionId));
    }
  };

  if (page === 'pre') {
    return (
      <PreScanPage
        housings={DEFAULT_HOUSINGS}
        housing={housing}
        onHousingChange={setHousing}
        engineReady={cassetteConfigReady && scanner.engineReady}
        errorMessage={cassetteConfigError ?? scanner.error?.message}
        onStart={startScan}
        onEnableOrientation={scanner.enableOrientation}
        cameraDeviceId={cameraDeviceId}
        onCameraDeviceIdChange={setCameraDeviceId}
        torchEnabled={torchEnabled}
        onTorchChange={setTorchEnabled}
        hScale={hScale}
        onHScaleChange={setHScale}
        framesToCapture={framesToCapture}
        onFramesToCaptureChange={setFramesToCapture}
        collectionMode={collectionMode}
        onCollectionModeChange={setCollectionMode}
      />
    );
  }

  if (page === 'scan') {
    return (
      <ScanPage
        scanner={scanner}
        previewMode={previewMode}
        onPreviewModeChange={handlePreviewModeChange}
        readerAspect={readerAspect}
        onReaderAspectChange={handleReaderAspectChange}
        onCancel={() => {
          scanner.stop();
          setPage('pre');
        }}
      />
    );
  }

  const currentScan = completedScans[completedScans.length - 1];

  // Guard: shouldn't normally happen, but handle gracefully
  if (!currentScan) {
    return null;
  }

  return (
    <PostScanPage
      currentScan={currentScan}
      completedScans={completedScans}
      pmfDiagnostics={pmfDiagnostics}
      onDeleteScan={(id) => { void handleDeleteScan(id); }}
      onScanAgain={() => setPage('pre')}
    />
  );
};

export default ImagingTest;
